import { Textarea, type TextareaProps } from '@mantine/core';
import {
  type FieldValues,
  type UseControllerProps,
  useController,
} from 'react-hook-form';
import { useRules } from './hooks';

export type TextareaFieldProps<T extends FieldValues> = UseControllerProps<T> &
  Omit<TextareaProps, 'value' | 'defaultValue'>;

/**
 * TextareaField component
 *
 * @example
 * ```tsx
 * <TextareaField name="field.name" required />
 * ```
 *
 * @public
 * @param name - field name
 * @param control - form control
 * @param defaultValue - default value
 * @param rules - validation rules
 * @param shouldUnregister - unregister field when unmount
 * @param onChange - onChange event
 * @param required - is field required
 * @param props - other props
 */
export function TextareaField<T extends FieldValues>({
  name,
  control,
  defaultValue,
  rules,
  shouldUnregister,
  onChange,
  required,
  ...props
}: TextareaFieldProps<T>) {
  const innerRules = useRules({ required, ...rules });
  const {
    field: { value, onChange: fieldOnChange, ...field },
    fieldState,
  } = useController<T>({
    name,
    control,
    defaultValue,
    rules: innerRules,
    shouldUnregister,
  });

  return (
    <Textarea
      error={fieldState.error?.message}
      value={value || ''}
      onChange={e => {
        fieldOnChange(e);
        onChange?.(e);
      }}
      {...field}
      {...props}
    />
  );
}
